import { useState, useEffect } from 'react';
import { companyService, eventService, guestService } from 'services/api';
import SearchableSelect from 'components/SearchableSelect';
import Paginacion from 'components/Paginacion';
import styles from './EventReports.module.css';

const PAGE_SIZE = 25;

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function DownloadButtons({ event }) {
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'xlsx' | null

  const download = async (type) => {
    setDownloading(type);
    try {
      const r = type === 'pdf'
        ? await guestService.downloadReportPdf(event.id)
        : await guestService.downloadReport(event.id);
      const mime = type === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      const safe = (event.name || 'evento').replace(/\s+/g, '_');
      a.href = url;
      a.download = `Asistencia_${safe}.${type}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // eslint-disable-next-line no-alert
      alert('No se pudo descargar el informe.');
    } finally { setDownloading(null); }
  };

  return (
    <div className={styles.botones}>
      <button className={`btn btn-sm btn-secondary btn-icon ${styles.botonPdf}`} title="Descargar PDF"
        disabled={!!downloading} onClick={() => download('pdf')}>
        {downloading === 'pdf' ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-file-pdf" />}
      </button>
      <button className={`btn btn-sm btn-secondary btn-icon ${styles.botonExcel}`} title="Descargar Excel"
        disabled={!!downloading} onClick={() => download('xlsx')}>
        {downloading === 'xlsx' ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-file-excel" />}
      </button>
    </div>
  );
}

export default function EventReports() {
  const [companies, setCompanies] = useState([]);
  const [filterComp, setFilterComp] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    companyService.getAll({ limit: 200 }).then(r => setCompanies(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filterComp) { setEvents([]); return; }
    setLoading(true);
    eventService.getAll({ company_id: filterComp })
      .then(r => setEvents(r.data?.data || r.data || []))
      .finally(() => setLoading(false));
  }, [filterComp]);

  // Paginación en el navegador (el servidor devuelve todos los eventos de la compañía).
  const totalPages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = events.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const cambiarCompania = (id) => { setFilterComp(id); setPage(1); };
  const irAPagina = (n) => { setPage(n); window.scrollTo({ top: 0 }); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-file-lines ${styles.iconoCabecera}`} />Reportes de eventos</h1>
          <p>Informe de asistencia por evento — para entregar a la compañía organizadora</p>
        </div>
        <div className={styles.filtro}>
          <i className={`fas fa-briefcase ${styles.iconoFiltro}`} />
          <SearchableSelect value={filterComp} onChange={cambiarCompania} placeholder="Selecciona una compañía" minWidth={220} maxWidth={300} ariaLabel="Compañía"
            options={companies.map(c => ({ value: c.id, label: c.name }))} />
        </div>
      </div>

      <div className="card">
        <div className={`card-body ${styles.sinRelleno}`}>
          {!filterComp ? (
            <div className="empty-state">
              <i className="fas fa-briefcase" />
              <p>Selecciona una compañía para ver sus eventos.</p>
            </div>
          ) : loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : !events.length ? (
            <div className="empty-state">
              <i className="fas fa-calendar-days" />
              <p>Esta compañía no tiene eventos registrados todavía.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Evento</th><th>Fecha</th><th>Duración</th><th>Lugar</th><th>Informe</th></tr>
                </thead>
                <tbody>
                  {pageRows.map(e => (
                    <tr key={e.id}>
                      <td>{e.name}</td>
                      <td>{fmtFecha(e.start_time)}</td>
                      <td>{e.duration_days > 1 ? `${e.duration_days} días` : '1 día'}</td>
                      <td>{e.address || '—'}</td>
                      <td><DownloadButtons event={e} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {!loading && filterComp && <Paginacion pagina={safePage} total={events.length} porPagina={PAGE_SIZE} onCambiar={irAPagina} />}
    </div>
  );
}
